import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { LicenseStatuses } from "@src/appV2/Licenses/api/types";
import { invalidateBookabilityStatus } from "@src/appV2/OpenShifts/ShiftAction/api/useBookabilityStatus";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

export const addLicensePayloadSchema = z.object({
  qualification: z.string(),
  state: z.string(),
  multiState: z.boolean(),
  number: z.string(),
  expiresAt: z.string().optional(),
});
export type AddLicenseRequestPayload = z.infer<typeof addLicensePayloadSchema>;

const addLicenseResponseSchema = z.object({
  id: z.string(),
  status: z.nativeEnum(LicenseStatuses),
  duplicate: z.boolean().optional(),
});

type AddLicenseResponse = z.infer<typeof addLicenseResponseSchema>;

export function useAddLicense(
  workerId: string,
  options: UseMutationOptions<AddLicenseResponse, AxiosError, AddLicenseRequestPayload> = {}
): UseMutationResult<AddLicenseResponse, AxiosError, AddLicenseRequestPayload> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AddLicenseRequestPayload) => {
      const response = await post({
        url: `${environmentConfig.REACT_APP_LICENSE_MANAGER_SERVICE_URL}/workers/${workerId}/licenses`,
        data: payload,
        requestSchema: addLicensePayloadSchema,
        responseSchema: addLicenseResponseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.ADD_LICENSE_FAILURE,
    },
    onSuccess: async () => {
      await Promise.all([
        invalidateGetWorkerQuery(queryClient),
        invalidateBookabilityStatus(queryClient),
      ]);
    },
    ...options,
  });
}
